import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, MessageCircle, Clock, X, Truck, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;

const buildNumeroWhatsApp = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("55")) return digits;
  return `55${digits}`;
};

const formatHora = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const formatDataBR = (iso) => {
  if (!iso) return "";
  const s = iso.slice(0, 10);
  const [a, m, d] = s.split("-");
  return `${d}/${m}/${a}`;
};

export default function AvisoSemanalBanner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/aviso-semanal/notas`);
      setData(data);
    } catch (e) {
      // silencioso — não polui a home
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  // Só aparece dentro da janela (Qua-Sab) e se tem notas
  if (!data || !data.dentro_da_janela || data.total_notas === 0) return null;

  const todosEnviados =
    data.total_notas > 0 && data.total_enviadas === data.total_notas;
  const isQuarta = data.dia_semana_hoje === "quarta";
  const restantes = data.total_notas - data.total_enviadas;

  // Estilo do botão principal
  let btnClasses = "";
  let icon = null;
  let label = "";
  if (todosEnviados) {
    btnClasses = "bg-gray-200 hover:bg-gray-300 text-gray-600 border-gray-300";
    icon = <CheckCircle2 className="h-5 w-5" />;
    label = "✅ Todos avisados";
  } else if (isQuarta) {
    btnClasses =
      "bg-amber-400 hover:bg-amber-500 text-amber-950 border-amber-500 shadow-md";
    icon = <AlertTriangle className="h-5 w-5" />;
    label = "Enviar Aviso de Atendimento para Próxima Rota";
  } else {
    // Qui/Sex/Sab e ainda não enviou tudo → vermelho pulsante
    btnClasses =
      "bg-red-500 hover:bg-red-600 text-white border-red-600 shadow-lg animate-pulse";
    icon = <AlertTriangle className="h-5 w-5" />;
    label = "ATENÇÃO — Envie os avisos para a próxima rota";
  }

  const handleClickWhatsApp = async (cliente, nota) => {
    const numero = buildNumeroWhatsApp(cliente.telefone);
    if (!numero) {
      toast.error("Cliente sem telefone");
      return;
    }
    const primeiro = cliente.primeiro_nome || (cliente.cliente_nome || "").split(" ")[0] || "";
    const msg = `Oi ${primeiro} Tudo bem!! `;
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(msg)}`;
    try {
      await axios.post(
        `${API}/aviso-semanal/click-whatsapp/${nota.promissoria_id}`
      );
    } catch (e) {
      // não bloqueia abertura do WA
    }
    window.open(url, "_blank");
    load();
  };

  const handleToggle = async (nota) => {
    try {
      await axios.post(
        `${API}/aviso-semanal/toggle-enviado/${nota.promissoria_id}`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao alternar aviso");
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="aviso-semanal-banner-btn"
        disabled={loading}
        className={`w-full max-w-4xl mx-auto mb-4 rounded-2xl border-2 px-5 py-4 flex items-center justify-between gap-3 font-bold text-sm sm:text-base transition-all ${btnClasses}`}
      >
        <div className="flex items-center gap-3 flex-1 text-left">
          {icon}
          <span className="uppercase tracking-wide">{label}</span>
        </div>
        <span
          className={`text-xs sm:text-sm rounded-full px-3 py-1 font-bold ${
            todosEnviados
              ? "bg-white/60 text-gray-700"
              : isQuarta
              ? "bg-white/70 text-amber-900"
              : "bg-white/80 text-red-700"
          }`}
        >
          {data.total_enviadas}/{data.total_notas}
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl max-h-[92vh] overflow-y-auto"
          data-testid="aviso-semanal-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <AlertTriangle className={`h-6 w-6 ${todosEnviados ? "text-gray-400" : "text-amber-500"}`} />
              Aviso de Atendimento — Próxima Rota
            </DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Notas com vencimento entre {formatDataBR(data.periodo_inicio)} e{" "}
              {formatDataBR(data.periodo_fim)} · {restantes} pendente(s) de aviso
            </p>
          </DialogHeader>

          <div className="space-y-6 pt-2">
            {data.por_carro.length === 0 && (
              <p className="text-center text-gray-400 py-10">
                Nenhuma nota a vencer nos próximos 5 dias.
              </p>
            )}

            {data.por_carro.map((carro) => (
              <div key={carro.carro_id} data-testid={`aviso-carro-${carro.carro_id}`}>
                <div className="flex items-center gap-2 mb-2 pb-1 border-b-2 border-indigo-200">
                  <Truck className="h-5 w-5 text-indigo-600" />
                  <h3 className="font-bold text-lg text-indigo-900 uppercase">
                    {carro.carro_nome}
                  </h3>
                </div>

                {carro.rotas.map((rota) => (
                  <div key={rota.rota_id} className="mb-4">
                    <div className="flex items-center gap-1 mb-2 mt-2">
                      <MapPin className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-semibold text-purple-800">
                        {rota.rota_nome}
                      </span>
                    </div>
                    <div className="space-y-2 pl-1">
                      {rota.clientes.map((cli) => (
                        <div
                          key={cli.cliente_id}
                          data-testid={`aviso-cliente-${cli.cliente_id}`}
                          className="rounded-xl border bg-white shadow-sm p-3 space-y-2"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm truncate">
                                {cli.cliente_nome}
                              </p>
                              <p className="text-xs text-gray-500">
                                {cli.cidade_estado} · {cli.telefone}
                              </p>
                            </div>
                          </div>

                          {cli.notas.map((nota) => {
                            const enviado = nota.aviso?.enviado;
                            const clickEm = nota.aviso?.click_whatsapp_em;
                            return (
                              <div
                                key={nota.promissoria_id}
                                data-testid={`aviso-nota-${nota.promissoria_id}`}
                                className={`rounded-lg border p-2 flex flex-col sm:flex-row sm:items-center gap-2 ${
                                  enviado
                                    ? "bg-emerald-50/70 border-emerald-200"
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <div className="flex-1 text-xs">
                                  <span className="font-semibold">
                                    Nota #{nota.numero_pedido}
                                  </span>
                                  <span className="text-gray-500 ml-1">
                                    · Venc: {formatDataBR(nota.data_vencimento)}
                                  </span>
                                  <span className="ml-1 font-bold text-red-600">
                                    · R${" "}
                                    {(nota.valor_restante || 0).toFixed(2)}
                                  </span>
                                </div>

                                <div className="flex items-center gap-2 flex-wrap justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleClickWhatsApp(cli, nota)
                                    }
                                    className="gap-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50 h-8"
                                    data-testid={`btn-wa-${nota.promissoria_id}`}
                                  >
                                    <MessageCircle className="h-3.5 w-3.5" />
                                    WhatsApp
                                  </Button>

                                  <div
                                    className={`flex items-center gap-1 text-xs rounded-md px-2 py-1 min-w-[70px] justify-center ${
                                      clickEm
                                        ? "bg-emerald-100 text-emerald-800"
                                        : "bg-gray-200 text-gray-500"
                                    }`}
                                    data-testid={`contador-${nota.promissoria_id}`}
                                  >
                                    <Clock className="h-3 w-3" />
                                    {clickEm ? formatHora(clickEm) : "--:--"}
                                  </div>

                                  <button
                                    onClick={() => handleToggle(nota)}
                                    data-testid={`toggle-enviado-${nota.promissoria_id}`}
                                    className={`relative inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold transition-colors border-2 ${
                                      enviado
                                        ? "bg-emerald-500 text-white border-emerald-600"
                                        : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                                    }`}
                                  >
                                    <span
                                      className={`h-2.5 w-2.5 rounded-full ${
                                        enviado ? "bg-white" : "bg-gray-400"
                                      }`}
                                    />
                                    {enviado ? "ENVIADO" : "ENVIADO?"}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="gap-1"
              data-testid="aviso-close-btn"
            >
              <X className="h-4 w-4" /> Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
